import { Button, CloseIcon, FilterIcon, Modal, Typography } from '@gurupay/gp-ui-library';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FiltersModalProps {
	children: ReactNode;
	onClear: () => void;
	onSubmit: () => void;
	hasFilters?: boolean;
}

export const FiltersModal = ({
	children,
	hasFilters = false,
	onClear,
	onSubmit,
}: FiltersModalProps) => {
	const { t } = useTranslation();
	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const handleOpen = () => {
		setIsModalOpen(true);
	};

	const handleClear = () => {
		onClear();
		setIsModalOpen(false);
	};

	const handleSubmit = () => {
		onSubmit();
		setIsModalOpen(false);
	};

	useEffect(() => {
		document.body.style.overflow = isModalOpen ? 'hidden' : '';

		return () => {
			document.body.style.overflow = '';
		};
	}, [isModalOpen]);

	useEffect(() => {
		if (!isModalOpen) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsModalOpen(false);
			}
		};

		document.addEventListener('keydown', handleKeyDown, true);

		return () => {
			document.removeEventListener('keydown', handleKeyDown, true);
		};
	}, [isModalOpen]);

	return (
		<>
			<Button
				className="relative flex-row-reverse w-max"
				icon={<FilterIcon />}
				onClick={handleOpen}
				variant="secondary"
			>
				{t('filters.title')}
				{hasFilters && (
					<span className="absolute top-3 right-4 h-2 w-2 rounded-full bg-status-error-500" />
				)}
			</Button>

			<Modal
				className="max-w-lg px-0 py-space-16 w-full"
				isOpened={isModalOpen}
				overlayClassName="px-space-16 pt-[88px] pb-space-16 md:px-0 md:py-space-16"
			>
				<div className="flex justify-between border-b border-neutral-100 pb-space-8 m-0 px-space-24 max-mobile:mb-space-24">
					<Typography className="w-full text-center" level="h3">
						{t('filters.title')}
					</Typography>
					<CloseIcon className="cursor-pointer" onClick={handleClose} />
				</div>

				<form aria-label="Filters" onSubmit={handleSubmit}>
					<div className="px-space-24 space-y-space-16 min-mobile:pt-space-24 ml-space-4 max-mobile:mx-[-8px]">
						{children}
					</div>

					<div className="border-t border-neutral-100 flex gap-space-8 max-mobile:block mt-space-24 justify-between px-space-24">
						<Button
							className="mt-space-16 flex-row-reverse mb-space-16 max-mobile:w-full"
							disabled={!hasFilters}
							icon={<CloseIcon />}
							onClick={handleClear}
							type="button"
							variant="ghost"
						>
							{t('filters.clear')}
						</Button>

						<Button
							className="mt-space-16 flex-row-reverse mb-space-16 max-mobile:w-full"
							icon={<FilterIcon />}
							type="submit"
						>
							{t('filters.submit')}
						</Button>
					</div>
				</form>
			</Modal>
		</>
	);
};

FiltersModal.displayName = 'FiltersModal';
